/**
 * Chart-Vorlagen für das Creator Studio
 * Kompatibel mit recharts-Bibliothek
 * Enthält 6 verschiedene Diagrammtypen mit Beispieldaten
 */

import type { ChartElement } from './elementTypes';

/**
 * Standard-Farbpalette für Diagramme
 * Inspiriert von Tailwind-Farben, optimiert für Kontrast
 */
export const DEFAULT_CHART_COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#06B6D4', // cyan-500
  '#84CC16', // lime-500
];

/**
 * Pastel-Farbpalette für weichere Darstellungen
 */
export const PASTEL_CHART_COLORS = [
  '#93C5FD', // blue-300
  '#6EE7B7', // emerald-300
  '#FCD34D', // amber-300
  '#FCA5A5', // red-300
  '#C4B5FD', // violet-300
  '#F9A8D4', // pink-300
  '#67E8F9', // cyan-300
  '#BEF264', // lime-300
];

/**
 * Dunkle Farbpalette für kontrastreiche Darstellungen
 */
export const DARK_CHART_COLORS = [
  '#1D4ED8', // blue-700
  '#047857', // emerald-700
  '#B45309', // amber-700
  '#B91C1C', // red-700
  '#6D28D9', // violet-700
  '#BE185D', // pink-700
  '#0E7490', // cyan-700
  '#4D7C0F', // lime-700
];

/**
 * Alle Chart-Vorlagen
 */
export const CHART_TEMPLATES: ChartElement[] = [
  // Vertikales Balkendiagramm
  {
    id: 'chart-bar-vertical',
    name: 'Balkendiagramm (Vertikal)',
    type: 'chart',
    tags: ['balken', 'vergleich', 'kategorien', 'statistik'],
    category: 'Balken',
    chartType: 'bar',
    colors: DEFAULT_CHART_COLORS,
    defaultData: [
      { name: 'Jan', value: 4500, color: DEFAULT_CHART_COLORS[0] },
      { name: 'Feb', value: 3200, color: DEFAULT_CHART_COLORS[1] },
      { name: 'Mär', value: 5800, color: DEFAULT_CHART_COLORS[2] },
      { name: 'Apr', value: 4100, color: DEFAULT_CHART_COLORS[3] },
      { name: 'Mai', value: 6200, color: DEFAULT_CHART_COLORS[4] },
    ],
  },

  // Horizontales Balkendiagramm
  {
    id: 'chart-bar-horizontal',
    name: 'Balkendiagramm (Horizontal)',
    type: 'chart',
    tags: ['balken', 'horizontal', 'ranking', 'vergleich'],
    category: 'Balken',
    chartType: 'bar',
    colors: DEFAULT_CHART_COLORS,
    defaultData: [
      { name: 'Produkt A', value: 85, color: DEFAULT_CHART_COLORS[0] },
      { name: 'Produkt B', value: 72, color: DEFAULT_CHART_COLORS[1] },
      { name: 'Produkt C', value: 64, color: DEFAULT_CHART_COLORS[2] },
      { name: 'Produkt D', value: 48, color: DEFAULT_CHART_COLORS[3] },
    ],
  },

  // Liniendiagramm
  {
    id: 'chart-line',
    name: 'Liniendiagramm',
    type: 'chart',
    tags: ['linie', 'trend', 'zeitreihe', 'entwicklung'],
    category: 'Linie',
    chartType: 'line',
    colors: [DEFAULT_CHART_COLORS[0]],
    defaultData: [
      { name: 'Woche 1', value: 1200 },
      { name: 'Woche 2', value: 1800 },
      { name: 'Woche 3', value: 1600 },
      { name: 'Woche 4', value: 2400 },
      { name: 'Woche 5', value: 2100 },
    ],
  },

  // Flächendiagramm
  {
    id: 'chart-area',
    name: 'Flächendiagramm',
    type: 'chart',
    tags: ['fläche', 'trend', 'volumen', 'verlauf'],
    category: 'Fläche',
    chartType: 'area',
    colors: [DEFAULT_CHART_COLORS[0], DEFAULT_CHART_COLORS[1]],
    defaultData: [
      { name: 'Q1', value: 4200 },
      { name: 'Q2', value: 5800 },
      { name: 'Q3', value: 4900 },
      { name: 'Q4', value: 7200 },
    ],
  },

  // Kreisdiagramm
  {
    id: 'chart-pie',
    name: 'Kreisdiagramm',
    type: 'chart',
    tags: ['kreis', 'anteil', 'verteilung', 'prozent'],
    category: 'Kreis',
    chartType: 'pie',
    colors: DEFAULT_CHART_COLORS,
    defaultData: [
      { name: 'Desktop', value: 45, color: DEFAULT_CHART_COLORS[0] },
      { name: 'Mobile', value: 38, color: DEFAULT_CHART_COLORS[1] },
      { name: 'Tablet', value: 12, color: DEFAULT_CHART_COLORS[2] },
      { name: 'Andere', value: 5, color: DEFAULT_CHART_COLORS[3] },
    ],
  },

  // Donut-Diagramm
  {
    id: 'chart-donut',
    name: 'Donut-Diagramm',
    type: 'chart',
    tags: ['donut', 'ring', 'anteil', 'verteilung'],
    category: 'Kreis',
    chartType: 'donut',
    colors: DEFAULT_CHART_COLORS,
    defaultData: [
      { name: 'Organisch', value: 52, color: DEFAULT_CHART_COLORS[0] },
      { name: 'Direkt', value: 28, color: DEFAULT_CHART_COLORS[1] },
      { name: 'Referral', value: 15, color: DEFAULT_CHART_COLORS[2] },
      { name: 'Social', value: 5, color: DEFAULT_CHART_COLORS[3] },
    ],
  },
];

/**
 * Chart-Vorlage nach ID abrufen
 */
export function getChartTemplateById(id: string): ChartElement | undefined {
  return CHART_TEMPLATES.find((chart) => chart.id === id);
}

/**
 * Charts nach Typ filtern
 */
export function getChartsByType(
  chartType: ChartElement['chartType']
): ChartElement[] {
  return CHART_TEMPLATES.filter((chart) => chart.chartType === chartType);
}

/**
 * Charts nach Tags suchen
 */
export function searchCharts(query: string): ChartElement[] {
  const lowerQuery = query.toLowerCase();
  return CHART_TEMPLATES.filter(
    (chart) =>
      chart.name.toLowerCase().includes(lowerQuery) ||
      chart.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Verfügbare Chart-Typen
 */
export const CHART_TYPES = ['bar', 'line', 'pie', 'donut', 'area'] as const;
export type ChartType = (typeof CHART_TYPES)[number];

/**
 * Chart-Typ Labels für die UI
 */
export const CHART_TYPE_LABELS: Record<ChartType, string> = {
  bar: 'Balken',
  line: 'Linie',
  pie: 'Kreis',
  donut: 'Donut',
  area: 'Fläche',
};

/**
 * Verfügbare Farbpaletten
 */
export const COLOR_PALETTES = {
  default: DEFAULT_CHART_COLORS,
  pastel: PASTEL_CHART_COLORS,
  dark: DARK_CHART_COLORS,
} as const;

export type ColorPaletteName = keyof typeof COLOR_PALETTES;

/**
 * Hilfsfunktion: Farbe für einen Datenpunkt ermitteln
 * Verwendet die Farbe aus den Daten oder fallback auf Palette
 */
export function getDataPointColor(
  data: { color?: string },
  index: number,
  palette: string[] = DEFAULT_CHART_COLORS
): string {
  return data.color || palette[index % palette.length];
}

/**
 * Hilfsfunktion: Prozent-Formatierung für Labels
 */
export function formatPercentage(value: number, total: number): string {
  const percent = (value / total) * 100;
  return `${percent.toFixed(1)}%`;
}

/**
 * Hilfsfunktion: Summe aller Werte berechnen (für Kreisdiagramme)
 */
export function calculateTotal(
  data: Array<{ value: number }>
): number {
  return data.reduce((sum, item) => sum + item.value, 0);
}
